/* eslint-disable @typescript-eslint/no-deprecated */
'use client';
import {
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetTitle,
  BottomSheetTrigger,
} from '@svenskaspelab/ui/react/bottom-sheet';
import { Button } from '@svenskaspelab/ui/react/button';
import type { PropsWithChildren, ReactNode } from 'react';

import { cn } from '../utils/entry.js';

type BetslipProps = PropsWithChildren<{
  className: string;
  isPopulatedCoupon: boolean;
  betslipContent: ReactNode;
  betslipMobileHeight?: string;
  onOpenDrawerClick?: () => void;
}>;

/**
 * The betslip component, uses the existing mobile bottom sheet to display information about the coupon that is actively being filled.
 *
 * @param root0 input object
 * @param root0.children the children to pass down
 * @param root0.className the classname to be used
 * @param root0.isPopulatedCoupon if the coupon is populated at all.
 * @param root0.betslipContent the overview component to be displayed in the bottom sheet
 * @param root0.betslipMobileHeight the height of the betslip on mobile, defaults to 77px
 * @param root0.onOpenDrawerClick function to be called when the drawer is opened, used for tracking purposes
 * @returns the complete betslip
 */
export default function Betslip({
  children,
  className,
  isPopulatedCoupon,
  betslipContent,
  betslipMobileHeight,
  onOpenDrawerClick,
}: BetslipProps): ReactNode {
  const upwardXlShadow = 'shadow-[0_-20px_25px_-5px_rgba(0,0,0,0.1),0_-8px_10px_-6px_rgba(0,0,0,0.1)]';
  const classNames = [
    'grid',
    'bg-surface-base',
    'gap-x-[12px]',
    'p-[16px]',
    'lg:p-[12px]',
    'pt-[4px]',
    'lg:pt-[12px]',
    `h-[${betslipMobileHeight ?? '77px'}]`,
    'lg:h-[70px]',
    'text-layer-fg-muted',
  ];
  if (className.length > 0) {
    classNames.push(className);
  } else {
    classNames.push('grid-flow-col');
  }
  const handleOnClick = (): void => {
    if (onOpenDrawerClick) {
      onOpenDrawerClick();
    }
  };
  return (
    <>
      <div
        className={cn(
          'invisible fixed bottom-0 left-0',
          isPopulatedCoupon && 'visible z-40 hidden w-full lg:block',
          upwardXlShadow
        )}
      >
        <div className="bg-surface-base mx-auto w-full max-w-[1440px] px-[36px]">
          <div className={cn(classNames.join(' '), 'items-center')}>{children}</div>
        </div>
      </div>
      {/* eslint-disable @typescript-eslint/no-deprecated -- Betslip still requires the BottomSheet components until it can migrate to Drawer. */}
      <BottomSheet variant="modal">
        <BottomSheetTrigger onClick={handleOnClick} asChild>
          <div
            className={cn(
              'invisible fixed bottom-0 left-0',
              isPopulatedCoupon && 'bg-surface-base visible z-40 grid w-full cursor-pointer lg:hidden',
              upwardXlShadow
            )}
          >
            <div
              className={cn('bg-surface-base grid h-[8px] w-full items-end justify-items-center justify-self-center')}
            >
              <div className="bg-layer-fg-muted bottom-0 h-[2px] w-[5%] rounded" />
            </div>
            <div className={classNames.join(' ')}>{children}</div>
          </div>
        </BottomSheetTrigger>
        <BottomSheetContent
          closeAction={false}
          className="mb-3 max-h-[80vh] sm:max-h-[80vh] [&_[data-slot=bottom-sheet-handle]]:mt-4 [&_[data-slot=bottom-sheet-handle]]:mb-0 [&_[data-slot=bottom-sheet-handle]]:h-2 [&_[data-slot=bottom-sheet-handle]]:w-[100px]"
        >
          <BottomSheetHeader className="max-w-none min-w-0 gap-1.5 p-4 sm:max-w-none sm:min-w-0 sm:p-4">
            <BottomSheetTitle className="text-center text-lg">Kupongöversikt</BottomSheetTitle>
          </BottomSheetHeader>
          {betslipContent}
          <BottomSheetFooter className="max-w-none min-w-0 p-4 pt-0 sm:max-w-none sm:min-w-0 sm:flex-col sm:p-4 sm:pt-0">
            <BottomSheetClose asChild>
              <Button variant="outline">Stäng</Button>
            </BottomSheetClose>
          </BottomSheetFooter>
        </BottomSheetContent>
      </BottomSheet>
      {/* eslint-enable @typescript-eslint/no-deprecated */}
    </>
  );
}
